/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Misc imports
import { Button, notification, Space } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, WarningFilled, InfoCircleFilled } from '@ant-design/icons';
import { generateRandomString } from '../../string/generateRandomString';

const successColor = 'rgba(45, 178, 138, 1)';
const errorColor = 'rgba(210, 54, 54, 1)';
const warningColor = 'rgba(243, 167, 2, 1)';
const infoColor = 'rgba(96, 93, 236, 1)';

/**
 * Opens a console alert message in the form of a bottom notification
 * @param {string} type The notification type, supported types are 'success','error','warning' or 'info'
 * @param {string} message The message to be displayed
 * @param {string} description The description to be displayed (extra info)
 * @param {number} duration - The duration of the displayed message (defaults to 6 seconds - pass 0 or null if you want to persist the message until dismiss from user)
 * @param {object} primaryButton -A primary button to add an action in the message. Expects an action and a title in the object
 * @param {object} secondaryButton - A secondary button to add an action in the message. Expects an action and a title in the object
 */
const openConsoleAlertMessage = ({
  type = 'success',
  message,
  description,
  duration = 6,
  primaryButton = { action: null, title: null },
  secondaryButton = { action: null, title: null }
}) => {

  const msgKey = generateRandomString(6);

  let currentIcon = <CheckCircleFilled style={{ color: successColor }} />;

  if (type === 'error') {
    currentIcon = <CloseCircleFilled style={{ color: errorColor }} />;
  } else if (type === 'warning') {
    currentIcon = <WarningFilled style={{ color: warningColor }} />;
  } else if (type === 'info') {
    currentIcon = <InfoCircleFilled style={{ color: infoColor }} />;
  }

  const actionButtons = (primaryButton.action || secondaryButton.action)
    ? (
      <Space>
        {secondaryButton.action
          ? <Button
            size="small"
            onClick={() => {
              secondaryButton?.action();
              notification.close(msgKey);
            }}
          >
            {secondaryButton?.title}
          </Button>
          : null}
        {primaryButton.action
          ? <Button
            type="primary"
            size="small"
            onClick={() => {
              primaryButton?.action();
              notification.close(msgKey);
            }}
          >
            {primaryButton?.title}
          </Button>
          : null}
      </Space>
    ) : null;

  notification.open({
    key: msgKey,
    message: message,
    description: description,
    placement: 'bottomLeft',
    duration: duration,
    className: `console-message console-message-${type}`,
    icon: currentIcon,
    btn: actionButtons,
    closable: true
  });
};

export default openConsoleAlertMessage;
